import { useState } from 'react';
import { Link } from 'react-router';
import { useStore, useTranslation } from '../store/useStore';
import { Shield, Target, Globe, Award, ChevronRight, CheckCircle, Clock, Users, Star, MapPin, Languages, FileText } from 'lucide-react';

export default function IPME() {
  const { t } = useTranslation();
  const { user } = useStore();
  const [applied, setApplied] = useState(false);

  if (applied) {
    return (
      <div className="min-h-screen pt-24 pb-16 bg-[#f8fafc] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-20 h-20 mx-auto mb-6 bg-emerald-50 rounded-full flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-semibold text-[#1e293b] mb-2 font-display">Application Received!</h2>
          <p className="text-[#64748b] mb-6">
            Thank you for applying to become an IPME. Our admin team will review your application and respond within 5-7 business days.
          </p>
          <Link to="/" className="px-6 py-3 bg-[#c2410c] text-white rounded-xl hover:bg-[#9a3412] transition-colors text-sm font-semibold">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-16 bg-[#f8fafc]">
      {/* Hero */}
      <div className="relative bg-gradient-to-br from-[#312e81] via-[#4338ca] to-[#c2410c] py-20 mb-12">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 text-center text-white">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 text-white text-xs font-semibold mb-6">
            <Star className="w-3.5 h-3.5" /> Join Our Elite Network
          </div>
          <h1 className="text-4xl md:text-5xl font-semibold mb-4 font-display">Become an IPME</h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            Join the Instant Purchase Marketing Executive program and earn commissions by verifying deals and onboarding suppliers in your city.
          </p>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 lg:px-8">
        {/* Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {[
            { icon: <Target className="w-8 h-8" />, title: 'Verify Deals', desc: 'Review and verify supplier deals in your area of operation. Ensure quality and compliance.' },
            { icon: <Users className="w-8 h-8" />, title: 'Onboard Suppliers', desc: 'Help new suppliers join the platform. Guide them through verification and deal upload.' },
            { icon: <Award className="w-8 h-8" />, title: 'Earn Commissions', desc: 'Earn 10% of the IPO margin on every deal you verify. Plus recurring commissions on follow-ups.' },
          ].map((benefit) => (
            <div key={benefit.title} className="bg-white rounded-2xl border border-gray-100 p-8 text-center hover:shadow-lg transition-all hover:-translate-y-1">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-hero flex items-center justify-center text-white">
                {benefit.icon}
              </div>
              <h3 className="text-lg font-semibold text-[#1e293b] mb-2">{benefit.title}</h3>
              <p className="text-sm text-[#64748b] leading-relaxed">{benefit.desc}</p>
            </div>
          ))}
        </div>

        {/* How It Works */}
        <div className="mb-16">
          <h2 className="text-3xl font-semibold text-[#1e293b] text-center mb-10 font-display">How the IPME Program Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Apply', desc: 'Submit your application with CV, city of operation, and languages.' },
              { step: '02', title: 'Get Approved', desc: 'Admin reviews your application and approves your IPME status.' },
              { step: '03', title: 'Receive Tasks', desc: 'Deals are automatically assigned to you based on your city.' },
              { step: '04', title: 'Earn', desc: 'Verify deals and earn commissions on every successful transaction.' },
            ].map((item) => (
              <div key={item.step} className="relative text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-[#0f172a] flex items-center justify-center text-white text-lg font-bold font-display">
                  {item.step}
                </div>
                <h3 className="text-base font-semibold text-[#1e293b] mb-2">{item.title}</h3>
                <p className="text-sm text-[#64748b]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Application Form */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl border border-gray-100 p-8">
            <h2 className="text-2xl font-semibold text-[#1e293b] mb-1 font-display">Apply Now</h2>
            <p className="text-sm text-[#64748b] mb-6">Fill in your details to apply for the IPME program</p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#1e293b] mb-1.5">IPO Username</label>
                <input type="text" defaultValue={user?.email?.split('@')[0]} readOnly className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-[#64748b]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1e293b] mb-1.5">City of Operation *</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94a3b8]" />
                  <input type="text" placeholder="e.g., Accra" className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1e293b] mb-1.5">Languages Spoken</label>
                <div className="relative">
                  <Languages className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94a3b8]" />
                  <input type="text" placeholder="e.g., English, French, Swahili" className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1e293b] mb-1.5">Brief CV / Experience</label>
                <textarea placeholder="Describe your sales, marketing, or business development experience..." rows={4} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm resize-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1e293b] mb-1.5">Supporting Documents</label>
                <div className="p-4 border-2 border-dashed border-gray-200 rounded-xl hover:border-[#c2410c]/30 transition-colors text-center">
                  <FileText className="w-8 h-8 text-[#94a3b8] mx-auto mb-2" />
                  <p className="text-sm text-[#64748b]">Drag and drop files here, or click to browse</p>
                </div>
              </div>
              <button
                onClick={() => setApplied(true)}
                className="w-full py-3.5 bg-[#c2410c] text-white rounded-xl hover:bg-[#9a3412] transition-colors text-sm font-semibold mt-4"
              >
                Submit Application
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
